import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { alertsApi } from '../api'
import type { AlertHistory } from '../api'
import { Button, Skeleton, Card, PageHeader, EmptyState } from '../components'
import {
  Bell,
  Mail,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Activity,
  ExternalLink,
  MessageSquare,
  Webhook,
  Filter,
  X,
} from 'lucide-react'

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

const ALERT_TYPES = [
  { value: '', label: 'All Types' },
  { value: 'error', label: 'Error' },
  { value: 'critical', label: 'Critical' },
  { value: 'warning', label: 'Warning' },
  { value: 'recovery', label: 'Recovery' },
  { value: 'info', label: 'Info' },
]

export default function AlertsPage() {
  const { teamId } = useParams<{ teamId: string }>()
  const navigate = useNavigate()
  const [alerts, setAlerts] = useState<AlertHistory[]>([])
  const [total, setTotal] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [page, setPage] = useState(0)
  const [alertType, setAlertType] = useState('')
  const limit = 20

  useEffect(() => {
    const loadAlerts = async () => {
      setIsLoading(true)
      try {
        const response = await alertsApi.getHistory({
          skip: page * limit,
          limit,
          alert_type: alertType || undefined,
        })
        setAlerts(response.alerts)
        setTotal(response.total)
      } catch (err) {
        console.error('Failed to load alerts:', err)
      } finally {
        setIsLoading(false)
      }
    }
    loadAlerts()
  }, [page, alertType])

  // Reset page when filter changes
  const handleFilterChange = (type: string) => {
    setAlertType(type)
    setPage(0)
  }

  const clearFilters = () => {
    setAlertType('')
    setPage(0)
  }

  const hasActiveFilters = alertType !== ''

  const getAlertIcon = (alertType: string) => {
    switch (alertType) {
      case 'error':
        return <AlertTriangle className="w-5 h-5 text-red-600" />
      case 'critical':
        return <XCircle className="w-5 h-5 text-red-700" />
      case 'recovery':
        return <CheckCircle className="w-5 h-5 text-green-600" />
      case 'warning':
        return <Activity className="w-5 h-5 text-yellow-600" />
      case 'info':
        return <Mail className="w-5 h-5 text-blue-600" />
      default:
        return <Bell className="w-5 h-5 text-slate-500" />
    }
  }

  const getAlertTypeBadge = (alertType: string) => {
    const variants: Record<string, { label: string; className: string }> = {
      error: { label: 'Error', className: 'bg-red-100 text-red-700' },
      critical: { label: 'Critical', className: 'bg-red-200 text-red-800' },
      recovery: { label: 'Recovery', className: 'bg-green-100 text-green-700' },
      warning: { label: 'Warning', className: 'bg-yellow-100 text-yellow-700' },
      info: { label: 'Info', className: 'bg-blue-100 text-blue-700' },
    }
    const variant = variants[alertType] || {
      label: alertType,
      className: 'bg-slate-100 text-slate-700',
    }
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${variant.className}`}>
        {variant.label}
      </span>
    )
  }

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case 'email':
        return <Mail className="w-3.5 h-3.5" />
      case 'slack':
        return <MessageSquare className="w-3.5 h-3.5" />
      case 'discord':
        return <MessageSquare className="w-3.5 h-3.5" />
      case 'webhook':
        return <Webhook className="w-3.5 h-3.5" />
      default:
        return <Bell className="w-3.5 h-3.5" />
    }
  }

  const handleAlertClick = (alert: AlertHistory) => {
    const appId = alert.metadata?.app_id || alert.resource_id
    const errorGroupId = alert.metadata?.error_group_id

    if (appId && errorGroupId) {
      navigate(`/t/${teamId}/apps/${appId}/errors/${errorGroupId}`)
    } else if (appId) {
      navigate(`/t/${teamId}/apps/${appId}`)
    }
  }

  const isClickable = (alert: AlertHistory) => {
    return !!(alert.metadata?.app_id || alert.resource_id)
  }

  const totalPages = Math.ceil(total / limit)

  return (
    <>
      <PageHeader
        title="Alert History"
        subtitle="View all notifications sent for your applications"
      />

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4">
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <Filter className="w-4 h-4" />
          <span>Filter:</span>
        </div>

        {/* Alert Type Filter */}
        <select
          value={alertType}
          onChange={e => handleFilterChange(e.target.value)}
          className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          {ALERT_TYPES.map(type => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </select>

        {/* Clear filters button */}
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="inline-flex items-center gap-1 px-2 py-1 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded"
          >
            <X className="w-3.5 h-3.5" />
            Clear
          </button>
        )}

        {/* Results count */}
        <span className="text-sm text-slate-500 ml-auto">
          {total} alert{total !== 1 ? 's' : ''}
        </span>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <Card key={i} className="p-4">
              <div className="flex items-center gap-4">
                <Skeleton className="w-10 h-10 rounded-lg" />
                <div className="flex-1">
                  <Skeleton className="h-5 w-64 mb-2" />
                  <Skeleton className="h-4 w-48" />
                </div>
                <Skeleton className="h-4 w-20" />
              </div>
            </Card>
          ))}
        </div>
      ) : alerts.length === 0 ? (
        <EmptyState
          icon={<Bell className="w-6 h-6" />}
          title={hasActiveFilters ? 'No matching alerts' : 'No alerts yet'}
          description={
            hasActiveFilters
              ? 'Try adjusting your filters to see more results'
              : 'Alerts will appear here when we send notifications about your apps'
          }
        />
      ) : (
        <>
          <div className="space-y-2">
            {alerts.map(alert => {
              const clickable = isClickable(alert)
              return (
                <Card
                  key={alert.id}
                  variant={clickable ? 'interactive' : 'default'}
                  className={`p-4 ${clickable ? 'cursor-pointer hover:border-slate-300' : ''}`}
                  onClick={clickable ? () => handleAlertClick(alert) : undefined}
                >
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      {getAlertIcon(alert.alert_type)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      {/* Title row */}
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-sm font-medium text-slate-900 truncate">
                          {alert.title}
                        </h3>
                        {clickable && (
                          <ExternalLink className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                        )}
                      </div>

                      {/* App name and message */}
                      <div className="text-sm text-slate-600 mb-2">
                        {alert.metadata?.app_name && (
                          <span className="font-medium text-slate-700">
                            {alert.metadata.app_name}
                          </span>
                        )}
                        {alert.metadata?.app_name && alert.message && (
                          <span className="mx-1.5 text-slate-400">·</span>
                        )}
                        <span className="truncate">
                          {alert.message.slice(0, 100)}
                          {alert.message.length > 100 ? '...' : ''}
                        </span>
                      </div>

                      {/* Badges row */}
                      <div className="flex items-center gap-2 flex-wrap">
                        {getAlertTypeBadge(alert.alert_type)}

                        {/* Channel badges */}
                        <div className="flex items-center gap-1">
                          {alert.channels_sent.map(channel => (
                            <span
                              key={channel}
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-xs"
                              title={channel}
                            >
                              {getChannelIcon(channel)}
                            </span>
                          ))}
                        </div>

                        {/* Extra context */}
                        {alert.metadata?.level && alert.alert_type === 'error' && (
                          <span className="text-xs text-slate-500">
                            Level: {alert.metadata.level}
                          </span>
                        )}
                        {alert.metadata?.health_url && (
                          <span className="text-xs text-slate-500 truncate max-w-[200px]">
                            {alert.metadata.health_url}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Time */}
                    <div className="flex-shrink-0 text-right">
                      <span className="text-sm text-slate-500">
                        {formatRelativeTime(alert.sent_at)}
                      </span>
                      <div className="text-xs text-slate-400 mt-0.5">
                        {new Date(alert.sent_at).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </div>
                    </div>
                  </div>
                </Card>
              )
            })}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <p className="text-sm text-slate-600">
                Showing {page * limit + 1}-{Math.min((page + 1) * limit, total)} of {total} alerts
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 0}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page >= totalPages - 1}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </>
  )
}
